/**
 * Reports > Stock (user, 2026-10-23, "NeXseed Create New Report.xlsx"): four plant-level stock
 * summaries the plants kept as Excel pivots: Warehouse Wise Stock (Raw / Condition / Pack in each
 * warehouse of the plant's Warehouse master), Veg. Seed Stock by DOGR (Date of GOT Result), Treated
 * & Untreated, and Germination & GOT Status. Each returns the shared `ReportResult`, so the screen,
 * Excel and PDF are the warehouse reports' own builders.
 *
 * They are pivots: the rows arrive already grouped, with their subtotal and grand-total rows marked
 * by `STOCK_ROW_LEVEL`, and a parent's name printed only on its first row, as the sheet does. The
 * Warehouse Wise and DOGR column groups depend on the data (one per warehouse holding stock, one per
 * GOT result date), so those come back on the result (`ReportResult.columns`).
 */
import type {
  ReportCellFormat,
  ReportColumn,
  ReportDef,
} from "./warehouse-reports";

export const STOCK_REPORT_SLUGS = [
  "warehouse-wise",
  "dogr",
  "treatment",
  "germination-got",
] as const;
export type StockReportSlug = (typeof STOCK_REPORT_SLUGS)[number];
export type StockReportKey = `stock-${StockReportSlug}`;

export const isStockReportSlug = (s: string): s is StockReportSlug =>
  (STOCK_REPORT_SLUGS as readonly string[]).includes(s);
export const stockReportKey = (slug: StockReportSlug): StockReportKey =>
  `stock-${slug}`;
/** A Stock report's key. */
export const isStockReportKey = (k: string): k is StockReportKey =>
  (STOCK_REPORT_SLUGS as readonly string[]).some((s) => `stock-${s}` === k);

/** The row cell that marks a pivot's subtotal ("<Crop> Total") and grand-total rows. */
export const STOCK_ROW_LEVEL = "rowLevel";
export type StockRowLevel = "subtotal" | "total";

const c = (
  key: string,
  label: string,
  format: ReportCellFormat,
  more: Partial<Omit<ReportColumn, "key" | "label" | "format">> = {}
): ReportColumn => ({
  key,
  label,
  format,
  align:
    format === "int" || format === "qty" || format === "pct"
      ? "right"
      : format === "text"
        ? "left"
        : "center",
  ...more,
});

const CROP = c("crop", "Crop", "text");
const VAR_TYPE = c("varType", "Var. Type", "text");
const VARIETY = c("variety", "Variety", "text");
const qty = (key: string, label: string, group?: string) =>
  c(key, label, "qty", group ? { group } : {});

/** Warehouse Wise: the fixed head; each warehouse's Raw / Condition / Pack / Total go after it. */
export const STOCK_WH_HEAD: readonly ReportColumn[] = [CROP, VAR_TYPE, VARIETY];
export const STOCK_WH_TAIL: readonly ReportColumn[] = [
  qty("raw", "Raw", "Total"),
  qty("condition", "Condition", "Total"),
  qty("pack", "Pack", "Total"),
  // Root stock (docs/designs/root-stock-packing.md): the seeds of counted packed stock.
  c("packNos", "Pack Nos", "int", { group: "Total" }),
  qty("qty", "Grand Total"),
];
/** The stages a Warehouse Wise group splits into, in the sheet's order. */
export const STOCK_STAGES = [
  { key: "raw", label: "Raw" },
  { key: "condition", label: "Condition" },
  { key: "pack", label: "Pack" },
] as const;
export type StockStage = (typeof STOCK_STAGES)[number]["key"];
/** One warehouse's column group, keyed `wh:<locationId>:<stage>` (`total` for its own total). */
export const warehouseColumns = (
  whId: number | string,
  name: string
): ReportColumn[] => [
  ...STOCK_STAGES.map((s) => qty(`wh:${whId}:${s.key}`, s.label, name)),
  qty(`wh:${whId}:total`, "Total", name),
];

/** DOGR: the fixed head; each month's GOT result dates and its Total go after it. */
export const STOCK_DOGR_HEAD: readonly ReportColumn[] = [
  CROP,
  VARIETY,
  qty("qty", "Grand Total"),
];
/** One GOT result date's column (`d:YYYY-MM-DD`), headed by its day under its month. */
export const dogrDayColumn = (date: string, month: string) =>
  qty(`d:${date}`, date.slice(8, 10), month);
/** A month's total column (`m:YYYY-MM`). */
export const dogrMonthColumn = (month: string, label: string) =>
  qty(`m:${month}`, "Total", label);

const AS_ON_NOTE =
  "Quantities are the stock on the As on date; QC, GOT and treatment are as recorded today.";

export const STOCK_REPORTS: {
  [K in StockReportKey]: ReportDef & { key: K };
} = {
  "stock-warehouse-wise": {
    key: "stock-warehouse-wise",
    menu: "Warehouse Wise Stock Report",
    title: "Warehouse Wise Stock Report",
    asOn: true,
    kind: "flat",
    filters: ["asOnDate", "vertical", "crop"],
    columns: [...STOCK_WH_HEAD, ...STOCK_WH_TAIL],
    tooMany: "Too many lots. Choose a vertical or crop.",
    note: `One column group per warehouse in the plant's Warehouse master that holds stock on the date; Unbinned is stock at the plant with no bin. Var. Type is the variety's classification (F1 = Hybrid; Not set when the variety has none, as coded seed does). ${AS_ON_NOTE}`,
  },
  "stock-dogr": {
    key: "stock-dogr",
    menu: "Veg. Seed Stock DOGR Report",
    title: "Veg. Seed Stock DOGR Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "vertical", "crop"],
    columns: [...STOCK_DOGR_HEAD],
    tooMany: "Too many GOT results. Shorten the period or choose a crop.",
    note: "Seed by its Date of GOT Result (DOGR): the stock of the lot's lineage at the plant at the end of the day its final GOT result was reviewed. A retested lineage counts once, on its latest result.",
  },
  "stock-treatment": {
    key: "stock-treatment",
    menu: "Treated & Untreated Report",
    title: "Treated & Untreated Report",
    asOn: true,
    kind: "flat",
    filters: ["asOnDate", "vertical", "crop", "variety", "classification"],
    columns: [
      c("serial", "S.no.", "int", { align: "center" }),
      CROP,
      VARIETY,
      c("varType", "Classification", "text"),
      qty("condTreated", "Treated", "Condition"),
      qty("condUntreated", "Un-Treated", "Condition"),
      qty("condTotal", "Total", "Condition"),
      qty("rawUntreated", "Un-Treated", "Raw"),
      qty("allUntreated", "Un-Treated", "Con. + Raw"),
      qty("qty", "Grand Total"),
      qty("primedQty", "Primed"),
    ],
    tooMany: "Too many lots. Choose a vertical or crop.",
    note: `One row per variety of each crop, with the variety's classification (F1 = Hybrid; Not set when the variety has none, as coded seed does). Treated is condition seed whose Processing Slip applied a Treatment Schema; condition seed from arrival or the opening balance counts as untreated, and raw seed is always untreated. Pack seed counts with Condition. Primed is the part of the Grand Total a Priming slip returned (or made from such seed), raw or condition. ${AS_ON_NOTE}`,
  },
  "stock-germination-got": {
    key: "stock-germination-got",
    menu: "Germination & GOT Status",
    title: "Germination & GOT Status",
    asOn: true,
    kind: "flat",
    filters: ["asOnDate", "vertical", "crop"],
    columns: [
      CROP,
      c("variety", "Variety", "text"),
      qty("qty", "Total Stock"),
      qty("qcOk", "OK Qty", "Germination"),
      qty("qcBl", "BL Qty", "Germination"),
      qty("qcUt", "UT/RT Qty", "Germination"),
      qty("qcFail", "Fail Qty", "Germination"),
      qty("gotUt", "GOT UT Qty", "GOT"),
      qty("gotBl", "GOT BL Qty", "GOT"),
      qty("gotFail", "Fail Qty", "GOT"),
      qty("bothFail", "Both Fail Qty"),
    ],
    tooMany: "Too many lots. Choose a vertical or crop.",
    note: `Germination is the lot's latest QC result (its own, else the lot it was processed or packed from). BL is a germination test the lab verified Borderline that the QC supervisor has not closed yet; UT/RT is seed in the lab, sent for a retest, or not tested. GOT UT is seed whose GOT is required and has no final result; GOT BL is a reviewed Borderline result; GOT Fail is a reviewed Fail result (else a Fail declared at arrival). Both Fail is seed that failed germination and GOT. ${AS_ON_NOTE}`,
  },
};

export interface StockReportEntry {
  slug: StockReportSlug;
  key: StockReportKey;
  menu: string;
  hint: string;
}

const HINT: Record<StockReportSlug, string> = {
  "warehouse-wise":
    "Raw, Condition and Pack stock in each warehouse of the plant, crop, type and variety, as on a date",
  dogr: "Veg seed by its Date of GOT Result, day by day under each month",
  treatment:
    "Condition seed treated and untreated, and raw seed, by crop, variety and classification",
  "germination-got":
    "Stock by germination status (OK / BL / UT-RT / Fail), GOT UT / BL / Fail and both failed, crop and variety",
};

/** The Stock reports in the sheet's order, with what each tile says. */
export const STOCK_REPORT_LIST: readonly StockReportEntry[] =
  STOCK_REPORT_SLUGS.map((slug) => {
    const key = stockReportKey(slug);
    return { slug, key, menu: STOCK_REPORTS[key].menu, hint: HINT[slug] };
  });
